/**
 * Utility functions for week calculations in IRIP documents
 */

import moment from "moment";
import { getCurrentSchoolYearCalendar, fetchSchoolYearCalendar } from "../../query/calendarAPI";
import { getUserData } from "../../API";

/**
 * Calculates the current week number based on school year start date
 * @returns {Promise<number>} The week number since school year started (minimum 1)
 */
export const calculateWeekNumber = async () => {
  try {
    const userData = getUserData();
    
    // First get the current school year
    const currentSchoolYearResponse = await getCurrentSchoolYearCalendar({
      districtId: userData.district.id.toString(),
      schoolId: userData.schoolId,
    });
    
    if (!currentSchoolYearResponse?.getCurrentSchoolYearCalendar) {
      console.warn("Could not get current school year calendar");
      return 1; // Default to week 1
    }
    
    const schoolYear = currentSchoolYearResponse.getCurrentSchoolYearCalendar.schoolYear;
    
    // Then get the full school year calendar with start date
    const schoolYearCalendarResponse = await fetchSchoolYearCalendar({
      districtId: userData.district.id.toString(),
      schoolYear: schoolYear,
    });
    
    if (!schoolYearCalendarResponse?.schoolYearCalendar?.startDate) {
      console.warn("Could not get school year start date from calendar");
      return 1; // Default to week 1
    }
    
    const schoolYearStartDate = moment(schoolYearCalendarResponse.schoolYearCalendar.startDate);
    const currentDate = moment();
    
    // Calculate the number of weeks since school year started
    const weeksSinceStart = currentDate.diff(schoolYearStartDate, 'weeks') + 1;
    
    return Math.max(1, weeksSinceStart); // Ensure it's at least week 1
  } catch (error) {
    console.error("Error calculating week number:", error);
    return 1; // Default to week 1 on error
  }
};

/**
 * Calculates Saturday-Friday week boundaries for metrics
 * @param {Date} referenceDate - The date to calculate week boundaries for (default: today)
 * @returns {Object} Object with weekStart and weekEnd dates
 */
export const getWeekBoundaries = (referenceDate = new Date()) => {
  const currentDate = new Date(referenceDate);
  const dayOfWeek = currentDate.getUTCDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
  
  // Calculate days back to Saturday (6)
  // If today is Saturday (6), go back 0 days
  // If today is Sunday (0), go back 1 day  
  // If today is Monday (1), go back 2 days, etc.
  const daysBackToSaturday = dayOfWeek === 6 ? 0 : (dayOfWeek + 1);
  
  const weekStart = new Date(currentDate);
  weekStart.setUTCDate(currentDate.getUTCDate() - daysBackToSaturday);
  weekStart.setUTCHours(0, 0, 0, 0); // Saturday 12am UTC

  // Week end is Friday 11:59pm UTC
  const weekEnd = new Date(weekStart);
  weekEnd.setUTCDate(weekStart.getUTCDate() + 6); // Add 6 days to get to Friday
  weekEnd.setUTCHours(23, 59, 59, 999); // Friday 11:59pm UTC

  return { weekStart, weekEnd };
};

/**
 * Formats week boundaries as ISO date strings for API calls
 * @param {Date} referenceDate - The date to calculate week boundaries for (default: today)
 * @returns {Object} Object with weekStartDate and weekEndDate as ISO strings
 */
export const getWeekBoundariesISO = (referenceDate = new Date()) => {
  const { weekStart, weekEnd } = getWeekBoundaries(referenceDate);
  
  return {
    weekStartDate: weekStart.toISOString().split("T")[0],
    weekEndDate: weekEnd.toISOString().split("T")[0]
  };
}; 